import {
    Avatar,
    Field,
    FluentProvider,
    makeStyles,
    mergeClasses,
    Tag,
    TagPicker,
    TagPickerControl,
    TagPickerGroup,
    TagPickerInput,
    TagPickerList,
    TagPickerOption,
    useTagPickerFilter,
} from '@fluentui/react-components';
import { memo, useMemo, useRef, useState } from 'react';
import { type Root } from 'react-dom/client';
import { fetchQuery, graphql, RelayEnvironmentProvider, useRelayEnvironment } from 'react-relay';
import { relayEnv } from '../relay/relay-env';
import type {
    PeopleFieldPickerQuery,
    PeopleFieldPickerQuery$data,
} from './__generated__/PeopleFieldPickerQuery.graphql';
import { ThemeProvider, useThemeContext } from '../theme/ThemeContext';

interface IFieldPickerProps {
    label: string;
    list?: string;
    onChange: (value: TFieldPickerValue) => void;
    siteUrl?: string;
    translate: TranslateFn;
    value?: TFieldPickerValue;
}

export type TFieldPickerValue = Pick<
    NonNullable<NonNullable<PeopleFieldPickerQuery$data['getSharePointListFields']>[number]>,
    'displayName' | 'internalName'
>[];

const PeopleFieldPicker = memo<IFieldPickerProps>((props) => {
    const [input, setInput] = useState<string>('');
    const [open, setOpen] = useState<boolean>(false);
    const [options, setOptions] = useState<Map<string, TFieldPickerValue[number]>>(() => new Map());
    const [loading, setLoading] = useState<boolean>(false);
    const loadingRef = useRef(loading);
    const relayEnv = useRelayEnvironment();
    const styles = usePickerStyles();
    const { label, list, siteUrl, translate } = props;
    const disabled = !list || !siteUrl;
    const { theme } = useThemeContext();

    const selectedOptions = useMemo<string[]>(() => {
        if (props.value) {
            return props.value.map((entry) => entry.internalName);
        }
        return [];
    }, [props.value]);

    return (
        <FluentProvider theme={theme} applyStylesToPortals={true} className={styles.provider}>
            <Field
                hint={
                    loading
                        ? {
                              className: 'bio-properties-panel-description',
                              children: translate('Loading...'),
                          }
                        : 'Drücken Sie ENTER, um die Suche zu starten (mindestens 3 Zeichen eingeben)'
                }
                label={{
                    children: label,
                    className: 'bio-properties-panel-label',
                }}
            >
                <TagPicker
                    disabled={disabled}
                    noPopover={disabled}
                    onOpenChange={(_event, data) => {
                        if (data.open && !loadingRef.current) {
                            setLoading(true);
                            loadingRef.current = true;
                            fetchQuery<PeopleFieldPickerQuery>(
                                relayEnv,
                                query,
                                {
                                    list: list!,
                                    siteUrl: siteUrl!,
                                },
                                { fetchPolicy: 'store-or-network' }
                            )
                                .toPromise()
                                .then((queryResult) => {
                                    const fields =
                                        queryResult?.getSharePointListFields?.filter(
                                            (entry) => entry.peopleField
                                        ) || [];
                                    setOptions(
                                        new Map(fields.map((entry) => [entry.internalName, entry]))
                                    );
                                    loadingRef.current = false;
                                    setLoading(false);
                                })
                                .catch(() => {
                                    loadingRef.current = false;
                                    setLoading(false);
                                });
                        }
                        setOpen(data.open);
                    }}
                    onOptionSelect={(_event, data) => {
                        if (data.value === 'no-matches') {
                            return;
                        }

                        setInput('');

                        const allOptions = new Map(
                            [...(props.value || []), ...Array.from(options.values())].map(
                                (entry) => [entry.internalName, entry]
                            )
                        );

                        props.onChange(data.selectedOptions.map((val) => allOptions.get(val)!));
                    }}
                    open={disabled ? false : open}
                    selectedOptions={selectedOptions}
                >
                    <TagPickerControl
                        // className={styles.pickerCtrl}
                        className={mergeClasses(styles.pickerCtrl, 'bio-properties-panel-input')}
                        // expandIcon={{ className: styles.expandIcon }}
                    >
                        <TagPickerGroup
                            aria-label={translate('Selected {items}', { items: label })}
                            className={styles.pickerGroup}
                        >
                            {(props.value || []).map((entry) => (
                                <Tag
                                    key={entry.internalName}
                                    shape="rounded"
                                    media={
                                        <Avatar
                                            aria-hidden
                                            name={entry.displayName}
                                            color="colorful"
                                        />
                                    }
                                    primaryText={{ className: styles.tagPrimaryText }}
                                    value={entry.internalName}
                                >
                                    {entry.displayName}
                                </Tag>
                            ))}
                        </TagPickerGroup>

                        <TagPickerInput
                            aria-label={translate('Select {item}', { item: label })}
                            className={styles.pickerInput}
                            value={input}
                            onChange={(event) => {
                                setInput(event.target.value);
                            }}
                        />
                    </TagPickerControl>

                    <TagPickerList>
                        {useTagPickerFilter({
                            query: input,
                            options: loading ? [] : Array.from(options.keys()),
                            noOptionsElement: (
                                <TagPickerOption value="no-matches">
                                    {translate(loading ? 'Loading...' : 'No selection available')}
                                </TagPickerOption>
                            ),
                            renderOption: (key) => {
                                const option = options.get(key) || {
                                    displayName: key,
                                    internalName: key,
                                };
                                return (
                                    <TagPickerOption
                                        className={styles.tagPrimaryText}
                                        key={key}
                                        media={
                                            <Avatar
                                                shape="square"
                                                aria-hidden
                                                name={option.displayName}
                                                color="colorful"
                                            />
                                        }
                                        text={option.displayName}
                                        value={option.internalName}
                                    >
                                        <div className={styles.tagPrimaryText}>
                                            {option.displayName}
                                        </div>
                                    </TagPickerOption>
                                );
                            },
                            filter: (key) =>
                                !selectedOptions.includes(key) &&
                                options
                                    .get(key)!
                                    .displayName.toLowerCase()
                                    .includes(input.toLowerCase()),
                        })}
                    </TagPickerList>
                </TagPicker>
            </Field>
        </FluentProvider>
    );
});

const usePickerStyles = makeStyles({
    /*expandIcon: {
        minHeight: '25px',
        '>svg': {
            '>path': {
                stroke: 'black',
                strokeWidth: '0.11em',
            },
        },
    },*/
    provider: {
        backgroundColor: 'transparent',
    },
    pickerCtrl: {
        minHeight: 'unset',
        minWidth: 'unset',
        '::after': {
            display: 'none',
        },
        '&:hover': {
            'border-color': 'var(--input-border-color)',
        },
        '>span': {
            display: 'none',
            // minHeight: 'unset',
            // right: '4px',
        },
        /*'& .fui-TagPickerControl__expandIcon': {
            minHeight: '28px'
        }*/
    },
    pickerGroup: {
        padding: '4px 0',
        // paddingRight: '15px',
        '& >button': {
            //backgroundColor: tokens.colorNeutralBackground1,
            fontSize: 'smaller !important',
            height: 'unset',
            paddingLeft: '3px',
        },
    },
    pickerInput: {
        padding: 'unset',
    },
    tagPrimaryText: {
        //color: tokens.colorNeutralForeground1,
        whiteSpace: 'nowrap',
        overflowX: 'hidden',
        textOverflow: 'ellipsis',
    },
});

const query = graphql`
    query PeopleFieldPickerQuery($siteUrl: String!, $list: String!) {
        getSharePointListFields(siteUrl: $siteUrl, listId: $list) {
            displayName
            # fieldType
            internalName
            peopleField
        }
    }
`;

export const renderPeopleFieldPicker = (root: Root, props: IFieldPickerProps) => {
    root.render(
        <RelayEnvironmentProvider environment={relayEnv}>
            <ThemeProvider>
                <PeopleFieldPicker {...props} />
            </ThemeProvider>
        </RelayEnvironmentProvider>
    );
};
